import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useParams, useLocation, matchPath } from 'react-router-dom';
import PageTracker from './components/PageTracker'; 
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Home from "./components/Pages/Home";
import Story from "./components/Pages/Story";
import Contact from "./components/Pages/Contact";
import ImageUpload from "./components/ImageUpload";
import Models from "./components/Pages/Models";
import ModelView from "./components/Pages/ModelView";
import FAQ from "./components/Pages/Faq";
import ProcessPhases from "./components/Pages/How";
import System from "./components/Pages/System";
import HomeABTest from "./components/Pages/HomeABTest";
import InterestProfiles from "./components/InterestProfiles";
import Form from "./components/ContactForm";
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import { ContentProvider } from "./components/contentful-context";
import { LoadingProvider } from './context/LoadingContext';
import { DevToolsProvider } from './context/DevToolsContext';
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import ContactForm from "./components/ContactForm";
import InvestInUsForm from "./components/InvestInUsForm";
import ExperimentDebug from "./components/ExperimentDebug";
import DevToolsPanel from "./components/DevToolsPanel/DevToolsPanel";
import Backyard from "./components/Pages/Backyard";
import BackyardProposal from "./components/Pages/BackyardProposal";
import Partners from "./components/Pages/Partners";
import PartnerDetail from "./components/Pages/PartnerDetail";
import Why from "./components/Pages/Why";
import Blog from "./components/Pages/Blog";
import BlogPost from "./components/Pages/BlogPost";
import SpeckleViewerPage from "./components/Pages/SpeckleViewerPage";
import XlabsArchitecture from "./components/Pages/XlabsArchitecture";
import XlabsCompare from "./components/Pages/XlabsCompare";
import SpeckleBusinessCase from "./components/Pages/SpeckleBusinessCase";
import XlabsHub from "./components/Pages/XlabsHub";
import XlabsLocate from "./components/Pages/XlabsLocate";
import XlabsDesign from "./components/Pages/XlabsDesign";
import XlabsConfiguratorLayout from "./components/Xlabs/XlabsConfiguratorLayout";
import InternalAduUtahPjm from "./components/Pages/InternalAduUtahPjm";
import BuildconstruxPortfolio from "./components/Pages/BuildconstruxPortfolio";
import AuthGate from "./components/Auth/AuthGate";
import InternalIndex from "./components/Pages/InternalIndex";
import InternalProposals from "./components/Pages/InternalProposals";
import InternalUsers from "./components/Pages/InternalUsers";
import SimulationLab from "./components/Pages/SimulationLab";

/** Temporarily disable WIP / private routes (re-enable when ready to ship). */
const ENABLE_VIEWER_ROUTE = true;
const ENABLE_BACKYARD_PROPOSAL_ROUTE = false;
/**
 * Parked internal areas (media gallery, proposals, Utah PJM board).
 * While false these routes redirect to `/internal` — never to a public route.
 * See docs/INTERNAL_INDEX.md § Parked areas.
 */
const ENABLE_INTERNAL_PARKED_ROUTES = false;

/**
 * `/internal/*` tools are AuthGate-only — never redirect to public marketing
 * routes like `/product`. Auth is the gate; see docs/INTERNAL_INDEX.md.
 */

/** Parked `/internal/*` route: render the tool when enabled, else land on the index. */
function ParkedInternalRoute({ children }) {
  if (!ENABLE_INTERNAL_PARKED_ROUTES) {
    return <Navigate to="/internal" replace />;
  }
  return <AuthGate>{children}</AuthGate>;
}

const NotFound = () => (
  <div className="flex flex-col text-center py-10 h-screen justify-center">
    <h1 className="text-6xl">404</h1>
    <div className="text-xl">Sorry, this page does not exist</div>
    <div className="text-xl">
      Go back to the
      <Link to="/" className="underline">
        homepage
      </Link>
    </div>
  </div>
);

const Upload = () => (
  <div className="h-[800px]">
    <ImageUpload />
  </div>
);

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
};

const STANDALONE_ROUTES = [
  '/viewer',
  '/xlabs',
  '/xlabs-compare',
  '/xlabs/speckle-case',
  '/xlabs-hub',
  '/xlabs/locate',
  '/xlabs/design',
  '/backyard-austin',
  '/backyard-proposal',
  '/blog/the-2026-housing-reform-wave',
  '/internal',
  '/internal/proposals',
  '/internal/users',
  '/internal/adu-utah-pjm',
  '/internal/buildconstrux-media',
  '/internal/sim-lab',
];

function RedirectBuildersSlug() {
  const { slug } = useParams();
  return <Navigate to={`/partners/${slug}`} replace />;
}

function ConditionalNavbar() {
  const location = useLocation();
  const hidden = STANDALONE_ROUTES.some(p => matchPath(p, location.pathname));
  return hidden ? null : <Navbar />;
}

function ConditionalFooter() {
  const location = useLocation();
  const hidden = STANDALONE_ROUTES.some(p => matchPath(p, location.pathname));
  return hidden ? null : <Footer />;
}

function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <Router>
        <ScrollToTop />
        <PageTracker />
        <ContentProvider>
          <ConditionalNavbar />
          <LoadingProvider>
          <DevToolsProvider>
          <LoadingScreen />
          <main className="flex-grow">
            <Routes>
              {/* A/B Test: Homepage vs System */}
              <Route path="/" element={<Navigate to="/product" replace />} />
              
              {/* Interest profiles with Calendly */}
              <Route path="/interest" element={<InterestProfiles />} />
              
              {/* Direct access routes for testing */}
              <Route path="/home" element={<Navigate to="/product" replace />} />
              <Route path="/system" element={<System />} />
              <Route path="/backyard-austin" element={<Backyard />} />
              <Route
                path="/backyard-proposal"
                element={
                  ENABLE_BACKYARD_PROPOSAL_ROUTE ? (
                    <BackyardProposal />
                  ) : (
                    <Navigate to="/product" replace />
                  )
                }
              />
              
              <Route path="/story" element={<Story />} />
              <Route path="/product" element={<Why />} />
              <Route path="/why" element={<Navigate to="/product" replace />} />
              {/* <Route path="/contact" element={<Contact />} /> */}
              <Route path="/models" element={<Models />} />
              <Route path="/models/:name" element={<ModelView />} />
              <Route
                path="/viewer"
                element={
                  ENABLE_VIEWER_ROUTE ? (
                    <SpeckleViewerPage />
                  ) : (
                    <Navigate to="/product" replace />
                  )
                }
              />
              <Route path="/xlabs" element={<XlabsArchitecture />} />
              <Route path="/xlabs-compare" element={<XlabsCompare />} />
              <Route
                path="/xlabs/speckle-case"
                element={
                  <AuthGate>
                    <SpeckleBusinessCase />
                  </AuthGate>
                }
              />
              <Route path="/xlabs-hub" element={<XlabsHub />} />
              <Route element={<XlabsConfiguratorLayout />}>
                <Route path="/xlabs/locate" element={<XlabsLocate />} />
                <Route path="/xlabs/design" element={<XlabsDesign />} />
              </Route>
              <Route
                path="/internal"
                element={
                  <AuthGate>
                    <InternalIndex />
                  </AuthGate>
                }
              />
              <Route
                path="/internal/proposals"
                element={
                  <ParkedInternalRoute>
                    <InternalProposals />
                  </ParkedInternalRoute>
                }
              />
              <Route
                path="/internal/users"
                element={
                  <AuthGate>
                    <InternalUsers />
                  </AuthGate>
                }
              />
              <Route
                path="/internal/adu-utah-pjm"
                element={
                  <ParkedInternalRoute>
                    <InternalAduUtahPjm />
                  </ParkedInternalRoute>
                }
              />
              <Route
                path="/internal/buildconstrux-media"
                element={
                  <ParkedInternalRoute>
                    <BuildconstruxPortfolio />
                  </ParkedInternalRoute>
                }
              />
              <Route
                path="/internal/sim-lab"
                element={
                  <AuthGate>
                    <SimulationLab />
                  </AuthGate>
                }
              />
              <Route path="/partners/:slug" element={<PartnerDetail />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/builders/:slug" element={<RedirectBuildersSlug />} />
              <Route path="/builders" element={<Navigate to="/partners" replace />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/faqs" element={<FAQ />} />
              {/* <Route path="/how" element={<ProcessPhases />} /> */}
              <Route path="/contact/build-with-us/:formId" element={<ContactForm />} />
              <Route path="/contact/invest-in-us/:formId" element={<InvestInUsForm />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <DevToolsPanel />
          </DevToolsProvider>
          </LoadingProvider>
          <ConditionalFooter />
          
          {/* A/B Test Debug Panel */}
          <ExperimentDebug />
        </ContentProvider>
      </Router>
    </div>
  );
}

export default App;
